import { Box, Chip } from "@mui/material";
import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { memo, useCallback, useMemo } from "react";
import { useSalesContract } from "../../../../store/contexts/SalesContractContext";
import { ISelectType, Table, useFetch } from "@4uhub/lib4uhub";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { enUS, ptBR } from "date-fns/locale";
import MainApi from "../../../../services/mainApi.service";
import SituationChip from "../../Register/SalesChannel/SituationChip";
import { ISalesContractHcpInclusionRule } from "../../../../models/sales-contract-hcp-inclusion-rule";

const translationPath =
  "page.4uSalesContract.registers.salesContractHcpInclusionRule.";

const serviceURL =
  process.env.REACT_APP_MAIN + "/api/v1/SalesContractHcpInclusionRule/";
const service = new MainApi<ISalesContractHcpInclusionRule>(serviceURL);

export type TRowClick = GridRowParams<ISalesContractHcpInclusionRule>;

export const SalesContractHcpInclusionRulePage = memo(() => {
  const { onOpen, selectPropId, onRefresh, selectTabName, refresh } =
    useSalesContract();

  const { id } = useParams();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const translate = (field: string) => t(translationPath + field);

  const columns: GridColDef<ISalesContractHcpInclusionRule>[] = [
    {
      field: "healthProgram",
      headerName: translate("health_program"),
      renderCell: (params) => {
        const hp = params.row.healthProgram as ISelectType;
        return hp?.name ?? "-";
      },
    },
    {
      field: "initialDate",
      headerName: translate("initial_date"),
      renderCell: (params) => {
        if (!params.value) return "-";
        const date = new Date(params.value);
        date.setHours(date.getHours() + date.getTimezoneOffset() / 60);
        return format(date, "P", {
          locale: language === "pt-BR" ? ptBR : enUS,
        });
      },
    },
    {
      field: "endDate",
      headerName: translate("end_date"),
      renderCell: (params) => {
        if (!params.value) return "-";
        const date = new Date(params.value);
        date.setHours(date.getHours() + date.getTimezoneOffset() / 60);
        return format(date, "P", {
          locale: language === "pt-BR" ? ptBR : enUS,
        });
      },
    },
    {
      field: "enable",
      headerName: t(translationPath + "status") || "Status",
      maxWidth: 100,
      renderCell: (params) => {
        return (
          <Chip
            size="small"
            label={
              params.value
                ? t(translationPath + "active")
                : t(translationPath + "inactive")
            }
            color={params.value ? "success" : "error"}
          />
        );
      },
    },
  ];

  const handleAdd = () => {
    onOpen();
    selectTabName("salesContractHcpInclusionRule");
  };

  const rowClickHandler = useCallback(
    (params: GridRowParams<ISalesContractHcpInclusionRule>) => {
      selectPropId(params.row.id);
      onOpen();
      selectTabName("salesContractHcpInclusionRule");
    },
    [selectPropId, onOpen, selectTabName]
  );

  const props = useMemo(() => {
    return { SalesContractId: id, refresh };
  }, [id, refresh]);

  return (
    <Box sx={{ height: 600, width: "100%" }}>
      <Box height={600}>
        <Table<ISalesContractHcpInclusionRule>
          searchInput={false}
          showDefaultMacroFilters={false}
          columns={columns}
          service={service}
          onAdd={handleAdd}
          onRowClick={rowClickHandler}
          serviceProps={props}
          title={translate("title")}
          addButtonLabel={translate("add")}
          searchInputPlaceHolder={translate("search")}
          confirmOptions={{
            title: translate("confirm.title"),
            description: translate("confirm.description"),
            confirmationButtonProps: { variant: "contained" },
          }}
        />
      </Box>
    </Box>
  );
});
